import React, { useMemo, useState } from 'react';
import Modal from '../../../../components/Modal/Modal';
import TableTh from '../../../../components/TableTh/TableTh';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import IconSwitcher from '../../../../components/Icons/IconSwitcher/IconSwitcher';
import SelectGroupOne from '../../../../components/Forms/SelectGroup/SelectGroupOne';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import { ElementSize } from '../../../../types/element-size';
import { useDeletedCycleContract } from './hooks/useDeletedCycleContract';
import { DeletedCycleContractProps } from './interfaces/DeletedCycleContractItemProps';
import { useTranslation } from 'react-i18next';
import { DateFormats } from '../../../../enums/date-formats';
import { DateFormatter } from '../../../../utils/date-formatter';
import Order from '../../../../enums/order.enum';
import CycleStatus from '../../../../enums/cycle-status.enum';

type SortKey = 'name' | 'status' | 'deletedAt';
type SortState = { key: SortKey; option: Order };

const capitalize = (value: string) => value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '';

const RESTORE_STATUS_OPTIONS: { value: CycleStatus; label: string }[] = [
  { value: CycleStatus.Active, label: 'Active' },
  { value: CycleStatus.InActive, label: 'Inactive' },
  { value: CycleStatus.Completed, label: 'Completed' },
];

const DeletedCycleContractItem: React.FC<DeletedCycleContractProps> = ({ cycles, contractName }) => {
  const {
    canRestoreCycle,

    cycleToRestore,
    restoreTargetStatus,
    setRestoreTargetStatus,
    conflicts,
    isLoadingConflicts,
    openSelectedCycleRestore,
    closeSelectedCycleRestore,
    onAcceptSelectedCycleRestore,
  } = useDeletedCycleContract();
  const { t } = useTranslation();

  const hasActiveConflicts = conflicts.length > 0;
  const restoreStatusOptions = useMemo(() => {
    return RESTORE_STATUS_OPTIONS.map((option) => ({
      ...option,
      disabled: option.value === CycleStatus.Active && hasActiveConflicts,
    }));
  }, [hasActiveConflicts]);

  const [sort, setSort] = useState<SortState>({ key: 'deletedAt', option: Order.DESC });

  const changeSort = (key: string) => {
    const isSameField = sort.key === key;
    const newOrder = isSameField && sort.option === Order.ASC ? Order.DESC : Order.ASC;

    setSort({ key: key as SortKey, option: newOrder });
  };

  const sortedCycles = useMemo(() => {
    const multiplier = sort.option === Order.ASC ? 1 : -1;

    return [...cycles].sort((a, b) => {
      if (sort.key === 'deletedAt') {
        const timeA = a.deletedAt ? new Date(a.deletedAt).getTime() : 0;
        const timeB = b.deletedAt ? new Date(b.deletedAt).getTime() : 0;

        return (timeA - timeB) * multiplier;
      }

      if (sort.key === 'status') {
        return (a.status ?? '').localeCompare(b.status ?? '', undefined, { numeric: true }) * multiplier;
      }

      return a.name.localeCompare(b.name, undefined, { numeric: true }) * multiplier;
    });
  }, [cycles, sort]);

  const contractTitle = `${t('labels.contractName')}: ${contractName}`;

  return (
    <>
      <li className="c-deleted-cycle-contract-item flex flex-col relative overflow-hidden rounded-lg border border-foggy-mist bg-white p-2 shadow-15 dark:border-strokedark dark:bg-boxdark">
        <span className="c-deleted-cycle-contract-item__contract-name text-base font-bold max-md:pb-1 md:text-xl">{contractTitle}</span>
        {!!sortedCycles.length && (
          <div className="c-deleted-cycle-table text-center font-medium overflow-x-auto">
            <table className="c-deleted-cycle-table__table w-full">
              <thead className="c-deleted-cycle-table__thead text-nowrap text-xs leading-4 text-slate-gray dark:text-white">
                <tr>
                  <TableTh
                    classNameTh="sticky top-0 z-10 bg-white dark:bg-boxdark pl-4"
                    classNameBlock="justify-start"
                    option={{ KEY: 'name', NAME: 'Name' }}
                    dataSort={sort}
                    onClick={changeSort}
                  />
                  <TableTh
                    classNameTh="sticky top-0 z-10 bg-white dark:bg-boxdark"
                    classNameBlock="justify-center"
                    option={{ KEY: 'status', NAME: 'Deleted From' }}
                    dataSort={sort}
                    onClick={changeSort}
                  />
                  <TableTh
                    classNameTh="sticky top-0 z-10 bg-white dark:bg-boxdark"
                    classNameBlock="justify-center"
                    option={{ KEY: 'deletedAt', NAME: 'Deleted At' }}
                    dataSort={sort}
                    onClick={changeSort}
                  />
                  {canRestoreCycle && (
                    <th className="c-table-th sticky top-0 z-10 bg-white dark:bg-boxdark">
                      <div className="flex flex-row flex-nowrap justify-center">Restore</div>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="c-deleted-cycle-table__tbody max-md:text-xs max-md:leading-6">
                {sortedCycles.map((cycle, index) => {
                  const zebraColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-2';

                  return (
                    <tr key={cycle.id} className={`shadow-inset-table-top text-xs ${zebraColor}`}>
                      <td className="c-cycle-table__td py-2 pl-4 text-left">{cycle.name}</td>
                      <td className="c-cycle-table__td py-2">{capitalize(cycle.status)}</td>
                      <td className="c-cycle-table__td py-2">
                        {DateFormatter({ date: cycle.deletedAt, format: DateFormats['dd-MMM yyyy HH:mm:ss a'] })}
                      </td>
                      {canRestoreCycle && (
                        <td className="c-cycle-table__td py-2">
                          <div className="flex flex-row items-center justify-center">
                            <InteractiveButton
                              onClick={() => openSelectedCycleRestore(cycle)}
                              customType="min-h-fit min-w-fit max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer"
                              style={{ padding: '4px' }}
                            >
                              <div className="flex h-4 items-center justify-center text-success">
                                <IconSwitcher size={ElementSize.XXS} />
                              </div>
                            </InteractiveButton>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </li>
      <Modal
        modalTitle="Cycle Restoration"
        isVisible={!!cycleToRestore}
        acceptBtnText={t('commonText.restore')}
        onClose={closeSelectedCycleRestore}
        onAccept={onAcceptSelectedCycleRestore}
        showAcceptBtn
        showCancelBtn
        acceptBtnDisabled={
          isLoadingConflicts ||
          (restoreTargetStatus === CycleStatus.Active && hasActiveConflicts)
        }
      >
        {isLoadingConflicts ? (
          <div className="flex w-full items-center justify-center p-10">
            <SpinnerLoader className="h-10 w-10" />
          </div>
        ) : (
          <div className="flex flex-col gap-y-2 text-left">
            <p className="text-lg">
              This cycle was deleted from the <strong>{capitalize(cycleToRestore?.status)}</strong> section.
            </p>

            {hasActiveConflicts && (
              <div className="rounded border border-danger bg-danger/10 p-3 text-sm text-danger">
                <p className="font-bold">This cycle cannot be restored to Active:</p>
                <ul className="list-disc pl-5">
                  {conflicts.map((conflict) => (
                    <li key={conflict.channelItemId}>
                      {conflict.controllerName} channel {conflict.channelNumber} is already used by active cycle(s): {conflict.activeCycleNames.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <SelectGroupOne
              label="Restore to"
              name="restoreTargetStatus"
              handleChange={(event) => setRestoreTargetStatus(event.target.value as CycleStatus)}
              selectedOption={restoreTargetStatus ?? ''}
              options={restoreStatusOptions}
            />
          </div>
        )}
      </Modal>
    </>
  )
};

export default DeletedCycleContractItem;
